import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import type { ICategoryRepository } from '@app/domain/ports';
import type { Category, CategoryType } from '@app/domain/entities';

interface CategoryRow extends Omit<Category, 'hidden' | 'reducesOppositeType'> {
  hidden: number;
  reducesOppositeType: number;
}

export class CategoryRepository extends BaseSqliteRepository implements ICategoryRepository {
  private readonly SELECT_CLAUSE = `
    SELECT
      _id as id,
      CASE WHEN parent_category_id = 0 THEN NULL ELSE parent_category_id END as parentId,
      category_type as categoryType,
      name as name,
      is_hidden as hidden,
      position as position,
      reduces_opposite_type as reducesOppositeType,
      offset_category_id as offsetCategoryId,
      extended_data as extendedData
    FROM categories
  `;

  async findAll(): Promise<Category[]> {
    const rows = await this.query<CategoryRow>(
      `${this.SELECT_CLAUSE}
      WHERE is_deleted = 0
      ORDER BY categoryType ASC, parentId ASC, position ASC`,
    );
    return rows.map((row) => this.mapRowToCategory(row));
  }

  async getById(id: number): Promise<Category | null> {
    const row = await this.queryOne<CategoryRow>(
      `${this.SELECT_CLAUSE}
      WHERE _id = ? AND is_deleted = 0`,
      [id],
    );
    return row ? this.mapRowToCategory(row) : null;
  }

  async findByParent(parentId: number | null): Promise<Category[]> {
    const rows = await this.query<CategoryRow>(
      `${this.SELECT_CLAUSE}
      WHERE is_deleted = 0 AND parent_category_id = ?
      ORDER BY position ASC`,
      [parentId ?? 0],
    );
    return rows.map((row) => this.mapRowToCategory(row));
  }

  async findByType(categoryType: CategoryType): Promise<Category[]> {
    const rows = await this.query<CategoryRow>(
      `${this.SELECT_CLAUSE}
      WHERE is_deleted = 0 AND category_type = ?
      ORDER BY parentId ASC, position ASC`,
      [categoryType],
    );
    return rows.map((row) => this.mapRowToCategory(row));
  }

  async insert(category: Omit<Category, 'id'>): Promise<number> {
    const result = await this.queryOne<{ id: number }>(
      `INSERT INTO categories (
         name, parent_category_id, category_type, is_hidden, position,
         reduces_opposite_type, offset_category_id, extended_data
       )
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)
       RETURNING _id AS id`,
      [
        category.name,
        category.parentId ?? 0,
        category.categoryType,
        category.hidden ? 1 : 0,
        category.position,
        category.reducesOppositeType ? 1 : 0,
        category.offsetCategoryId ?? null,
        category.extendedData ?? null,
      ],
    );

    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO categories', [], 'Failed to insert category');
    }

    return result.id;
  }

  async update(category: Category): Promise<void> {
    await this.execute(
      `UPDATE categories
       SET name = ?,
           parent_category_id = ?,
           category_type = ?,
           is_hidden = ?,
           position = ?,
           reduces_opposite_type = ?,
           offset_category_id = ?,
           extended_data = ?,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [
        category.name,
        category.parentId ?? 0,
        category.categoryType,
        category.hidden ? 1 : 0,
        category.position,
        category.reducesOppositeType ? 1 : 0,
        category.offsetCategoryId ?? null,
        category.extendedData ?? null,
        category.id,
      ],
    );
  }

  async clearOffsetCategoryId(targetId: number): Promise<void> {
    await this.execute(
      `UPDATE categories
       SET offset_category_id = NULL,
           updated_at = strftime('%s','now')
       WHERE is_deleted = 0 AND offset_category_id = ?`,
      [targetId],
    );
  }

  async remapOffsetCategoryId(sourceId: number, targetId: number): Promise<void> {
    await this.execute(
      `UPDATE categories
       SET offset_category_id = ?,
           updated_at = strftime('%s','now')
       WHERE is_deleted = 0 AND offset_category_id = ?`,
      [targetId, sourceId],
    );
  }

  async delete(id: number): Promise<void> {
    await this.execute(
      `
      UPDATE categories
        SET
          is_deleted = (SELECT max(is_deleted)+1 FROM categories),
          updated_at = strftime('%s','now')
      WHERE
        is_deleted = 0 and _id = ?
      `,
      [id],
    );
  }

  private mapRowToCategory(row: CategoryRow): Category {
    return {
      ...row,
      hidden: Boolean(row.hidden), // Convert 0/1 to false/true
      reducesOppositeType: Boolean(row.reducesOppositeType),
      offsetCategoryId: row.offsetCategoryId ?? null,
    };
  }
}
